import { useProjectLoadingStore } from "@/components/common/project-provider/project-loading-store";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useLoadIElements } from "@/hooks/use-load-ielements";
import {
  Features,
  selectEnabledFeatures,
  selectHasFeature,
} from "@/store/features/features-slice";
import {
  // eslint-disable-next-line no-restricted-imports
  addLightweightElements,
} from "@/store/lightweight-elements-slice";
import { setRevisions } from "@/store/revisions-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { ProgressApiTracker } from "@/utils/progress-api-tracker";
import {
  DataManagementBaseProviders,
  DataManagementProvider,
  projectSettingsFetchStateSelector,
  useFetchProjectSettings,
} from "@faro-lotv/data-management";
import { LoadingScreen } from "@faro-lotv/flat-ui";
import { useTranslate } from "@faro-lotv/foreign-observers";
import { GUID, assert, toggleGuardsLogging } from "@faro-lotv/foundation";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import { IElementType } from "@faro-lotv/ielement-types";
import {
  initializeProject,
  selectCompanyId,
  selectProjectName,
} from "@faro-lotv/project-source";
import { ResponseSize, useApiClientContext } from "@faro-lotv/service-wires";
import { useCallback, useEffect, useMemo, type JSX } from "react";
import { useBeforeUnload, useParams, useSearchParams } from "react-router-dom";
import { DataPreparationUi } from "./data-preparation-ui";
import { useDataPreparationDeepLink } from "./deep-link/data-preparation-deep-link";
import { useLogRevisionChangesOnDev } from "./hooks/use-log-revision-changes-on-dev";
import { useLoadRevisionData } from "./loading/use-load-revision-data";
import { selectIsSavingHierarchyChanges } from "./store/data-preparation-ui/data-preparation-ui-selectors";
import { selectHasUnsavedEdits } from "./store/user-edits/data-preparation-user-edit-selectors";

/**
 * @returns Entry point of the data preparation page (aka the staging area).
 * It allows the user to register and publish new scans.
 */
export function DataPreparationPage(): JSX.Element | null {
  const { projectId } = useParams();
  const dispatch = useAppDispatch();
  assert(projectId, "No project ID provided");

  useEffect(() => {
    dispatch(initializeProject(projectId));
  }, [dispatch, projectId]);

  const { projectApiClient } = useApiClientContext();

  // Load the lightweight project structure in the background so that the
  // progress overview task cards in the cloud activity menu are shown correctly.
  // Also load the registration revisions to have the selectors for merged and newest revisions
  // populate correctly for the Live/Draft toggle button in the breadcrumbs.
  useEffect(() => {
    const ac = new AbortController();
    projectApiClient
      .getLightweightProjectStructure({ signal: ac.signal })
      .then((elements) => {
        if (elements.length > 0) {
          // getLightweightProjectStructure returns LightweightIElement[], not IElement[],
          // so addIElementsToStore cannot be used here.
          dispatch(addLightweightElements(elements));
        }
      })
      .catch(() => {
        // Silently ignore, not essential
      });
    projectApiClient.getAllRegistrationRevisions().then((revisions) => {
      dispatch(setRevisions(revisions));
    });
    return () => ac.abort();
  }, [dispatch, projectApiClient]);

  const [searchParams] = useSearchParams();
  const revisionId = searchParams.get("revisionId") ?? undefined;

  const isRegistrationDev = useAppSelector(
    selectHasFeature(Features.RegistrationDev),
  );
  // Enable logging of type validation errors in dev mode for easier debugging
  useEffect(() => {
    toggleGuardsLogging(isRegistrationDev);
  }, [isRegistrationDev]);

  const projectName = useAppSelector(selectProjectName);
  const translate = useTranslate();

  const title = useMemo(() => {
    if (!projectName) {
      return translate("Editor");
    }

    return translate("%{projectName} - Editor", { projectName });
  }, [projectName, translate]);
  useDocumentTitle(title);

  // Show a browser prompt when the user tries to close the tab if there are unsaved changes.
  const hasUnsavedEdits = useAppSelector(selectHasUnsavedEdits);
  const isSavingHierarchyChanges = useAppSelector(
    selectIsSavingHierarchyChanges,
  );
  const hasUnsavedChanges = hasUnsavedEdits || isSavingHierarchyChanges;

  const onBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        // Included for legacy support, e.g. Chrome/Edge < 119
        event.returnValue = true;
      }
    },
    [hasUnsavedChanges],
  );

  useBeforeUnload(onBeforeUnload);

  return (
    <>
      <DataPreparationLoading projectId={projectId} revisionId={revisionId} />
      <ProgressApiTracker />
    </>
  );
}

type DataPreparationLoadingProps = {
  /** Project identifier. */
  projectId: GUID;
  /** The ID of the revision containing the registration results. */
  revisionId?: GUID;
};

/** @returns Wrapper component which ensures that all needed data is loaded and validated. */
function DataPreparationLoading({
  projectId,
  revisionId,
}: DataPreparationLoadingProps): JSX.Element {
  useDataPreparationDeepLink();
  // For subsequent mounts after the initial load, the data is fetched in the background and the
  // loading screen is not shown.
  const {
    mustLoadRevisionData,
    revisionDataLoaded,
    mustLoadRootElement,
    rootElementLoaded,
  } = useProjectLoadingStore();
  const { revision, isLoading: isLoadingRevision } =
    useLoadRevisionData(revisionId);

  useLogRevisionChangesOnDev(revision);

  useFetchProjectSettings({ projectId });
  const stateProjectSettings = useAppSelector(
    projectSettingsFetchStateSelector,
  );
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = useAppSelector(selectCompanyId);

  const viewerFeatures = useAppSelector(selectEnabledFeatures);

  const isLoadingRoot = useLoadIElements([
    {
      types: [IElementType.projectRoot],
      expectedResponseSize: ResponseSize.small,
    },
  ]);

  // Data Management requires a logged-in user.
  const { isLoggedIn, requestLogin } = useAuthContext();
  useEffect(() => {
    if (!isLoggedIn) {
      requestLogin();
    }
  }, [isLoggedIn, requestLogin]);

  useEffect(() => {
    if (!isLoadingRoot) {
      rootElementLoaded(projectId);
    }
  }, [isLoadingRoot, projectId, rootElementLoaded]);

  useEffect(() => {
    if (!isLoadingRevision) {
      revisionDataLoaded(revisionId ?? "latest");
    }
  }, [isLoadingRevision, revisionDataLoaded, revisionId]);

  if (
    (mustLoadRevisionData(revisionId ?? "latest") && isLoadingRevision) ||
    (mustLoadRootElement(projectId) && isLoadingRoot) ||
    !currentUser ||
    !companyId ||
    stateProjectSettings === "pending" ||
    stateProjectSettings === "fetching"
  ) {
    return <LoadingScreen />;
  }

  return (
    <DataManagementBaseProviders viewerFeatures={viewerFeatures}>
      <DataManagementProvider
        projectId={projectId}
        companyId={companyId}
        currentUser={currentUser}
        isStandalone={false}
      >
        <DataPreparationUi
          revision={revision}
          currentUser={currentUser}
          companyId={companyId}
          projectId={projectId}
        />
      </DataManagementProvider>
    </DataManagementBaseProviders>
  );
}
